import { useState } from 'react';
import { useAuthStore } from '../../auth/store';
import { usePermissions, Permission } from '../../../shared/hooks/usePermissions';
import Button from '../../../shared/components/Button';
import { useCustomOrders } from '../hooks/useCustomOrders';
import type { CustomOrder, CustomOrderStatus } from '../api/customOrdersApi';
import { CustomOrderStatusBadge } from '../components/CustomOrderStatusBadge';
import { CustomOrderDetailModal } from '../components/CustomOrderDetailModal';
import { GuardrailSettingsModal } from '../components/GuardrailSettingsModal';
import { NewCustomOrderModal } from '../components/NewCustomOrderModal';
import { ExportButton } from "../../../shared/components/ExportButton";

const STATUS_FILTERS: { value: '' | CustomOrderStatus; label: string }[] = [
  { value: '', label: 'All' },
  { value: 'DRAFT', label: 'Draft' },
  { value: 'MEDIA_PROCESSING', label: 'Processing' },
  { value: 'AWAITING_PAYMENT', label: 'Awaiting payment' },
  { value: 'ORDER_CONFIRMED', label: 'Confirmed' },
  { value: 'ABANDONED', label: 'Abandoned' },
];

const formatMoney = (amountMinor: number | null | undefined, currency: string) => {
  if (amountMinor == null) return '—';
  const symbol = currency === 'NGN' ? '₦' : currency === 'USD' ? '$' : '';
  return `${symbol}${(amountMinor / 100).toLocaleString('en-NG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });

const th = 'px-6 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide';
const emptyBox =
  'text-center py-16 text-muted-foreground text-[14px] border border-dashed border-[#D9D9D9] rounded-[16px]';

export function CustomOrdersPage() {
  const selectedTenantId = useAuthStore((s) => s.selectedTenantId);
  const { hasPermission } = usePermissions();
  const canManage = hasPermission(Permission.ORDER_UPDATE_STATUS);

  const [status, setStatus] = useState<'' | CustomOrderStatus>('');
  const [searchInput, setSearchInput] = useState('');
  const [q, setQ] = useState('');
  const [openId, setOpenId] = useState<string | null>(null);
  const [guardrailsOpen, setGuardrailsOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);

  const { data: orders = [], isLoading, error } = useCustomOrders(selectedTenantId, {
    status: status || undefined,
    q: q || undefined,
  });

  const submitSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setQ(searchInput.trim());
  };

  return (
    <div className="py-6 max-w-6xl mx-auto px-2">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-[23px] font-bold text-foreground">Custom Orders</h1>
          <p className="text-[14px] text-muted-foreground mt-1">
            Review bespoke requests, set pricing, and mark orders ready for payment.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* PE-2127 — tenant-scoped export; the BFF requires an x-tenant-id, so
              this stays disabled until a merchant is selected. */}
          <ExportButton
            endpoint="/custom-orders/export"
            filenameBase="custom-orders"
            disabled={!selectedTenantId}
          />
          {selectedTenantId && canManage && (
            <>
              <Button variant="outline" size="sm" className="h-[40px]" onClick={() => setGuardrailsOpen(true)}>
                Upload limits
              </Button>
              {/* PE-2285 — the only place in Osprey that can WRITE custom-order assets. */}
              <Button size="sm" className="h-[40px]" onClick={() => setCreateOpen(true)}>
                New custom order
              </Button>
            </>
          )}
        </div>
      </div>

      {!selectedTenantId ? (
        <div className={emptyBox}>Select a merchant to manage their custom orders.</div>
      ) : (
        <>
          {/* Filters */}
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <div className="flex flex-wrap gap-1">
              {STATUS_FILTERS.map((f) => (
                <button
                  key={f.value || 'all'}
                  onClick={() => setStatus(f.value)}
                  className={`text-[13px] px-3 py-1.5 rounded-full transition-colors ${
                    status === f.value ? 'bg-[#28282A] text-white' : 'bg-muted text-muted-foreground hover:bg-muted'
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>
            <form onSubmit={submitSearch} className="ml-auto">
              <input
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search email / customer / order id"
                className="w-72 rounded-[10px] border border-[#D9D9D9] px-3 py-2 text-[14px] outline-none focus:border-[#7C5CFC]"
              />
            </form>
          </div>

          {isLoading ? (
            <div className="text-center py-16 text-muted-foreground">Loading custom orders…</div>
          ) : error ? (
            <div className="text-center py-16 text-red-500">Failed to load custom orders. Please try again.</div>
          ) : orders.length === 0 ? (
            <div className={emptyBox}>No custom orders match this view.</div>
          ) : (
            <div className="bg-card rounded-2xl border border-border overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border bg-muted text-left">
                    <th className={th}>Customer</th>
                    <th className={th}>Status</th>
                    <th className={th}>Assets</th>
                    <th className={th}>Price</th>
                    <th className={th}>Updated</th>
                    <th className={th}></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {orders.map((order: CustomOrder) => (
                    <tr
                      key={order.id}
                      className="hover:bg-muted transition-colors cursor-pointer"
                      onClick={() => setOpenId(order.id)}
                    >
                      <td className="px-6 py-4 text-sm text-foreground">
                        <div className="font-medium">{order.customerEmail}</div>
                        <div className="text-xs text-muted-foreground">#{order.id.slice(0, 8)}</div>
                      </td>
                      <td className="px-6 py-4">
                        <CustomOrderStatusBadge status={order.status} />
                      </td>
                      <td className="px-6 py-4 text-sm text-muted-foreground">{order.assets?.length ?? 0}</td>
                      <td className="px-6 py-4 text-sm text-foreground">{formatMoney(order.amountMinor, order.currency)}</td>
                      <td className="px-6 py-4 text-sm text-muted-foreground">{formatDate(order.lastActivityAt)}</td>
                      <td className="px-6 py-4 text-right">
                        <span className="text-xs text-blue-600 hover:underline">Review</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {openId && (
        <CustomOrderDetailModal
          orderId={openId}
          isOpen={!!openId}
          onClose={() => setOpenId(null)}
          canManage={canManage}
        />
      )}

      {selectedTenantId && canManage && (
        <NewCustomOrderModal isOpen={createOpen} onClose={() => setCreateOpen(false)} />
      )}

      {selectedTenantId && (
        <GuardrailSettingsModal
          tenantId={selectedTenantId}
          isOpen={guardrailsOpen}
          onClose={() => setGuardrailsOpen(false)}
        />
      )}
    </div>
  );
}
